import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, X, Plus, CheckCircle2, Loader } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LIFECYCLE_META = {
  trade_idea: { label: 'Trade Idea', color: '#6366f1', desc: 'Sharing a trade setup or idea' },
  doubt:      { label: 'Doubt',      color: '#f59e0b', desc: 'Unsure about a decision or setup' },
  backtest:   { label: 'Backtest',   color: '#8b5cf6', desc: 'Sharing backtest findings' },
  profit:     { label: 'Profit',     color: '#16c784', desc: 'A profitable trade outcome' },
  loss:       { label: 'Loss',       color: '#ef4444', desc: 'A losing trade — lessons learned' },
  mistake:    { label: 'Mistake',    color: '#f97316', desc: 'An error in judgment or execution' },
};

const ATTACH_LABELS = {
  backtest:        { label: 'Backtest Result',  color: '#6366f1' },
  pnl_summary:     { label: 'P&L Summary',      color: '#16c784' },
  trade_history:   { label: 'Trade Records',    color: '#06b6d4' },
  discipline:      { label: 'Discipline Score', color: '#f59e0b' },
  capital_tier:    { label: 'Capital Tier',     color: '#8b5cf6' },
};

function TagInput({ placeholder, tags, onChange }) {
  const [input, setInput] = useState('');
  const add = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) onChange([...tags, val]);
    setInput('');
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', minHeight: 40, alignItems: 'center' }}>
      {tags.map((t, i) => (
        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
          {t}
          <button onClick={() => onChange(tags.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', lineHeight: 1 }}>
            <X size={9} color="#818cf8" />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={tags.length === 0 ? placeholder : ''}
        style={{ flex: 1, minWidth: 80, background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: '#eceef5' }}
      />
    </div>
  );
}

export default function ForumCreatePage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lifecycle, setLifecycle] = useState('trade_idea');
  const [instruments, setInstruments] = useState([]);
  const [tags, setTags] = useState([]);

  const [attachable, setAttachable] = useState(null);
  const [attachLoading, setAttachLoading] = useState(true);
  const [selectedAttachments, setSelectedAttachments] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Pre-populate from navigate state (e.g. from BacktestPage "Share to Forum")
  useEffect(() => {
    if (location.state?.lifecycle) setLifecycle(location.state.lifecycle);
    if (location.state?.attachments) setSelectedAttachments(location.state.attachments);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API_URL}/forum/attachable`)
      .then(r => setAttachable(r.data))
      .catch(() => setAttachable({}))
      .finally(() => setAttachLoading(false));
  }, []);

  const toggleAttachment = (attach_type, snapshot) => {
    setSelectedAttachments(prev => {
      const exists = prev.find(a => a.attach_type === attach_type && JSON.stringify(a.snapshot) === JSON.stringify(snapshot));
      if (exists) return prev.filter(a => !(a.attach_type === attach_type && JSON.stringify(a.snapshot) === JSON.stringify(snapshot)));
      return [...prev, { attach_type, snapshot }];
    });
  };

  const isSelected = (attach_type, snapshot) =>
    selectedAttachments.some(a => a.attach_type === attach_type && JSON.stringify(a.snapshot) === JSON.stringify(snapshot));

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!description.trim()) { setError('Description is required.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/forum/posts`, {
        title: title.trim(),
        description: description.trim(),
        lifecycle,
        instruments,
        tags,
        attachments: selectedAttachments,
      });
      navigate(`/forum/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, display: 'block' };
  const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#eceef5', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh', paddingBottom: 80 }}>
      {/* Nav */}
      <div style={{ padding: '16px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate(-1)} style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeft size={15} color="#eceef5" />
        </button>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#eceef5' }}>Share a Strategy</span>
      </div>

      <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* Title */}
        <div>
          <label style={labelStyle}>Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. NIFTY breakout with volume confirmation" style={inputStyle} />
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>Description *</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Explain your strategy — setup, entry rules, exit plan, what you observed…" rows={5}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
        </div>

        {/* Lifecycle */}
        <div>
          <label style={labelStyle}>Stage</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(LIFECYCLE_META).map(([key, lm]) => (
              <button key={key} onClick={() => setLifecycle(key)}
                style={{
                  padding: '7px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  background: lifecycle === key ? lm.color + '22' : 'rgba(255,255,255,0.04)',
                  color: lifecycle === key ? lm.color : 'rgba(255,255,255,0.4)',
                  border: `1px solid ${lifecycle === key ? lm.color + '50' : 'rgba(255,255,255,0.1)'}`,
                }}>
                {lm.label}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 5 }}>{LIFECYCLE_META[lifecycle]?.desc}</p>
        </div>

        {/* Instruments */}
        <div>
          <label style={labelStyle}>Instruments (press Enter to add)</label>
          <TagInput placeholder="e.g. NIFTY50, RELIANCE…" tags={instruments} onChange={setInstruments} />
        </div>

        {/* Tags */}
        <div>
          <label style={labelStyle}>Tags (press Enter to add)</label>
          <TagInput placeholder="e.g. breakout, momentum…" tags={tags} onChange={setTags} />
        </div>

        {/* Attachments */}
        <div>
          <label style={labelStyle}>Attach Data (optional)</label>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 10 }}>
            Select data from your profile to share alongside the strategy. A snapshot is saved — it will not update after posting.
          </p>

          {attachLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Loader size={20} className="animate-spin" style={{ color: '#f59e0b' }} />
            </div>
          ) : attachable ? (
            <>
              {/* P&L Summary */}
              {attachable.pnl_summary?.total_trades > 0 && (() => {
                const s = attachable.pnl_summary;
                const sel = isSelected('pnl_summary', s);
                return (
                  <AttachOption
                    label="P&L Summary"
                    meta={`${s.total_trades} trades · ${s.win_rate_pct ?? '—'}% win rate · ₹${parseInt(s.total_pnl || 0).toLocaleString('en-IN')} total`}
                    color={ATTACH_LABELS.pnl_summary.color}
                    selected={sel}
                    onToggle={() => toggleAttachment('pnl_summary', s)}
                  />
                );
              })()}

              {/* Backtests */}
              {(attachable.recent_backtests || []).map((bt, i) => {
                const sel = isSelected('backtest', bt);
                return (
                  <AttachOption key={bt.id || i}
                    label={`Backtest — ${bt.symbol || bt.name}`}
                    meta={`${parseFloat(bt.total_return_pct || 0).toFixed(1)}% return · ${parseFloat(bt.win_rate_pct || 0).toFixed(0)}% win · ${bt.num_trades} trades`}
                    color={ATTACH_LABELS.backtest.color}
                    selected={sel}
                    onToggle={() => toggleAttachment('backtest', bt)}
                  />
                );
              })}

              {/* Discipline score */}
              {attachable.discipline_score != null && (() => {
                const s = { score: attachable.discipline_score, component_scores: attachable.discipline_components };
                const sel = isSelected('discipline', s);
                return (
                  <AttachOption
                    label="Discipline Score"
                    meta={`Score: ${attachable.discipline_score}`}
                    color={ATTACH_LABELS.discipline.color}
                    selected={sel}
                    onToggle={() => toggleAttachment('discipline', s)}
                  />
                );
              })()}

              {/* Capital tier */}
              {attachable.capital_snapshot && (() => {
                const s = attachable.capital_snapshot;
                const sel = isSelected('capital_tier', s);
                return (
                  <AttachOption
                    label="Capital Tier"
                    meta={`${s.tier || '—'} · ₹${parseInt(s.active_capital || 0).toLocaleString('en-IN')} daily cap`}
                    color={ATTACH_LABELS.capital_tier.color}
                    selected={sel}
                    onToggle={() => toggleAttachment('capital_tier', s)}
                  />
                );
              })()}

              {/* Recent trades */}
              {(attachable.recent_trades || []).length > 0 && (() => {
                const trades = attachable.recent_trades.slice(0, 10);
                const sel = isSelected('trade_history', trades);
                return (
                  <AttachOption
                    label="Recent Trades"
                    meta={`Last ${trades.length} closed trades`}
                    color={ATTACH_LABELS.trade_history.color}
                    selected={sel}
                    onToggle={() => toggleAttachment('trade_history', trades)}
                  />
                );
              })()}
            </>
          ) : (
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>No data available to attach yet.</p>
          )}
        </div>

        {/* Selected count */}
        {selectedAttachments.length > 0 && (
          <p style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>
            {selectedAttachments.length} attachment{selectedAttachments.length > 1 ? 's' : ''} selected
          </p>
        )}

        {/* Error */}
        {error && (
          <p style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>{error}</p>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !title.trim() || !description.trim()}
          style={{
            width: '100%', padding: '14px', borderRadius: 14, cursor: 'pointer',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            border: 'none', fontSize: 14, fontWeight: 800, color: '#fff',
            opacity: submitting || !title.trim() || !description.trim() ? 0.5 : 1,
          }}>
          {submitting ? 'Publishing…' : 'Publish Strategy'}
        </button>
      </div>
    </div>
  );
}

function AttachOption({ label, meta, color, selected, onToggle }) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 12px', borderRadius: 12, marginBottom: 7, cursor: 'pointer',
        background: selected ? color + '12' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${selected ? color + '40' : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
        background: selected ? color : 'rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected
          ? <CheckCircle2 size={13} color="#fff" />
          : <Plus size={12} color="rgba(255,255,255,0.3)" />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: selected ? '#eceef5' : 'rgba(255,255,255,0.5)', marginBottom: 1 }}>{label}</p>
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>{meta}</p>
      </div>
    </button>
  );
}
